import { useMemo } from "react";
import { ArrowRight, Download, Layers, Maximize2 } from "lucide-react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTopology, useSnapshot, useRecent, useAdapter } from "@/store/site";
import { useUiPref } from "@/store/uiPrefs";
import { exportSnapshotsCsv } from "@/lib/exportCsv";
import { IDLE_DEADBAND_W } from "@/lib/format";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { PageShell } from "./PageShell";
import { HealthBanner } from "@/components/HealthBanner";
import { WelcomeCard } from "@/components/WelcomeCard";
import { RightNowStrip } from "@/components/RightNowStrip";
import { StoryCards } from "@/components/StoryCards";
import { TariffWindowBanner } from "@/components/TariffWindowBanner";
import { SiteFlowDiagram } from "@/components/scada/SiteFlowDiagram";
import { Card, CardHeader, CardTitle, GranBadge } from "@/components/ui/Card";
import { PowerSparkChart, type PowerSparkSeriesData } from "@/components/charts/PowerSparkChart";
import { EmptyState } from "@/components/ui/EmptyState";
import { ChartSkeleton } from "@/components/ui/ChartSkeleton";
import { AlarmsCompact } from "@/components/AlarmsCompact";
import { ModeReasoningCard } from "@/components/ModeReasoningCard";
import { BessTile } from "@/components/BessTile";
import type { HistoricalQuery } from "@/data/types";

/**
 * The "Live energy flow" history card's window + granularity options.
 * Both selectors persist per site in ems-ui-prefs (PERS-09 / OVW-05) —
 * the chart is a dashboard widget, not a deep-link surface, so the URL
 * never carries them (precedence: pref → default). Defaults match what
 * operators most often want on opening the page: 72 h at 2-min
 * granularity (the edge's uplink cadence — every uplinked sample
 * shows).
 */
type PowerWindow = "24h" | "72h" | "30d";
type PowerGran = HistoricalQuery["granularity"] & ("2m" | "5m" | "15m" | "h");
type Density = "glance" | "detail";
const HOUR_MS = 3_600_000;
const POWER_WINDOWS: Record<PowerWindow, { label: string; short: string; ms: number }> = {
  "24h": { label: "Last 24 hours", short: "24 h", ms: 24 * HOUR_MS },
  "72h": { label: "Last 72 hours", short: "72 h", ms: 72 * HOUR_MS },
  "30d": { label: "Last 30 days", short: "30 d", ms: 30 * 24 * HOUR_MS },
};
const POWER_GRANS: Record<PowerGran, { label: string; short: string }> = {
  "2m":  { label: "2 min",   short: "2 m"  },
  "5m":  { label: "5 min",   short: "5 m"  },
  "15m": { label: "15 min",  short: "15 m" },
  "h":   { label: "1 hour",  short: "1 h"  },
};

// English labels above stay the data; the visible label/title strings are
// translated at render through these dict keys (shorts are unit tokens —
// never translated).
const WINDOW_LABEL_KEY: Record<PowerWindow, string> = {
  "24h": "monitor.window.last24h",
  "72h": "monitor.window.last72h",
  "30d": "monitor.window.last30d",
};
const GRAN_LABEL_KEY: Record<PowerGran, string> = {
  "2m":  "monitor.gran.2m",
  "5m":  "monitor.gran.5m",
  "15m": "monitor.gran.15m",
  "h":   "monitor.gran.1h",
};

/**
 * Sensible granularity options per window length. The 30-day window
 * with 2-min granularity would produce ~21 600 buckets — too many to
 * be useful and slow to render; we hide finer-than-15-min granularities
 * when the window is that long. Reciprocally, 1-hour granularity in
 * a 24-hour window only gives 24 points and reads as a stick figure;
 * we hide it to nudge the operator toward something denser.
 */
const ALLOWED_GRANS_PER_WINDOW: Record<PowerWindow, PowerGran[]> = {
  "24h": ["2m", "5m", "15m"],
  "72h": ["2m", "5m", "15m", "h"],
  "30d": ["15m", "h"],
};

export default function OverviewPage() {
  const topology = useTopology();
  const snap = useSnapshot();
  const recent = useRecent();
  const adapter = useAdapter();
  const t = useT();
  const hasPv = topology.smartmeters.some((m) => m.role === "pv");

  // Fetch the recent power history directly from the EMS server's
  // historical archive. Using the historical query (not the live
  // recent[] buffer) means the chart is populated immediately on
  // page load with a deep window. The operator can change the
  // granularity (AVG-aggregated by the EMS server) and the look-back
  // window via the two selectors above the chart. Defaults:
  // 72 h window at 2-min granularity — same data density as the
  // edge uplink cadence, so the curve shows every uplinked sample.
  // Stored prefs are re-validated against this build's option maps so
  // a stale value from an older build can't crash the selectors.
  const [storedWindow, setWindow] = useUiPref<PowerWindow>("overview.powerWindow", "72h");
  const [storedGran, setGran] = useUiPref<PowerGran>("overview.powerGran", "2m");
  // View density (F05): "glance" by default shows only the primary readings
  // (the at-a-glance health snapshot); "detail" restores every Overview
  // section. Re-validated defensively so a stale/garbage stored value can't
  // break rendering.
  const [density, setDensity] = useUiPref<Density>("overview.density", "glance");
  const dens: Density = density === "detail" ? "detail" : "glance";
  // SCADA diagram density (PERS-09): Detailed by default → today's exact view.
  const [scadaDetail, setScadaDetail] = useUiPref<boolean>("scada.detail", true);
  const window: PowerWindow = POWER_WINDOWS[storedWindow] ? storedWindow : "72h";
  const gran: PowerGran = POWER_GRANS[storedGran] ? storedGran : "2m";
  // If the user picks a window that doesn't allow the current
  // granularity, snap to the densest allowed one. This avoids
  // accidentally firing a 30-day @ 2-min query that would return tens
  // of thousands of points.
  const allowedGrans = ALLOWED_GRANS_PER_WINDOW[window];
  const effectiveGran: PowerGran = allowedGrans.includes(gran) ? gran : allowedGrans[0];
  // Rolling-window anchor. Keyed on a coarse 5-minute "now" bucket as
  // well as the window: keyed on [window] alone the anchor froze at
  // mount, so on a long-lived wallboard the chart stopped advancing and
  // the Open-in-Historical link below handed out an ever-staler window.
  // Re-renders land here regularly (live snapshot ticks every ~2 min),
  // so the first render in a fresh bucket re-anchors `end` to the
  // current wall clock; renders within the same bucket keep the memo —
  // and the bucketed query key below — perfectly stable.
  const nowBucket = Math.floor(Date.now() / 300_000);
  const range = useMemo(() => {
    const end = Date.now();
    return { start: end - POWER_WINDOWS[window].ms, end };
    // nowBucket is deliberately an unread dep: its only job is to roll
    // the anchor forward once per 5-minute bucket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window, nowBucket]);
  // The query key buckets `range.start`/`range.end` to the nearest
  // 5-minute mark so we don't refetch on every render — the anchor
  // re-snaps once per bucket but lands on arbitrary milliseconds, and a
  // 5-min granularity is plenty for cache identity at this zoom level.
  const cacheStart = Math.floor(range.start / 300_000) * 300_000;
  const cacheEnd = Math.floor(range.end / 300_000) * 300_000;
  const {
    data: history,
    isLoading: historyLoading,
    isError: historyIsError,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ["overview-power-history", topology.id, cacheStart, cacheEnd, effectiveGran, hasPv],
    queryFn: () => adapter.history({
      keys: hasPv
        ? ["grid_W", "battery/aggregation/power", "pv_power_W", "building_load_W"]
        : ["grid_W", "battery/aggregation/power", "building_load_W"],
      start_ts: range.start,
      end_ts: range.end,
      granularity: effectiveGran,
    }),
    staleTime: 60_000,    // 1 min — re-fetch when the page is revisited
    refetchInterval: 5 * 60_000,   // 5 min — pick up new buckets without polling too hard
    // When the rolling anchor moves to the next 5-min bucket the key
    // changes; keep showing the previous bucket's data while the new
    // fetch is in flight instead of flashing the skeleton every 5 min.
    placeholderData: keepPreviousData,
  });

  // Pass the historical response straight through as PER-SERIES arrays.
  // Each series has its own ts grid — no pseudo-snapshot assembly, no
  // zero-padding artifacts between buckets where individual keys emit
  // slightly different timestamps from the EMS server.
  const seriesData = useMemo<PowerSparkSeriesData>(() => ({
    grid:    history?.series.grid_W ?? [],
    battery: history?.series["battery/aggregation/power"] ?? [],
    pv:      history?.series.pv_power_W ?? [],
    load:    history?.series.building_load_W ?? [],
  }), [history]);

  const chartEmpty = !historyLoading
    && seriesData.grid.length === 0
    && seriesData.battery.length === 0
    && seriesData.load.length === 0;
  // Fetch error split from genuinely-empty (CONS-02): "Check that
  // AnalyticsEngineV2 is publishing…" is the wrong instruction when the
  // browser merely lost the TB connection. An error with stale non-empty
  // data keeps the chart (the data is still real history); an error with
  // nothing to show gets the honest connection message instead.
  const chartError = historyIsError && (history === undefined || chartEmpty);

  // "Open in Historical →" (LINK-08 / OVW-05): hand the card's CURRENT
  // window/granularity/keys 1:1 to the Historical analyzer. All keys are
  // curated on the receiving side, and every PowerGran value is a valid
  // Granularity token, so the mapping is direct. Explicit start/end
  // overrides win over any carried window.
  const openInHistoricalHref = carryTo("/historical", {
    keys: hasPv
      ? "grid_W,battery/aggregation/power,building_load_W,pv_power_W"
      : "grid_W,battery/aggregation/power,building_load_W",
    start: String(range.start),
    end: String(range.end),
    gran: effectiveGran,
    preset: "custom",
  });

  // N01 — single source of truth for the Overview's section order. Each
  // section is a named node rendered ONCE; Glance and Detail are two
  // orderings of the SAME nodes (no duplicated JSX branch that can drift, no
  // feature removed — every demoted card still renders, just lower).
  //
  //   Detail (unchanged, encyclopaedic): verdict → explainer cards → live
  //     state → story → SCADA → chart → fleet/alarms.
  //   Glance (verdict → live state → problems): verdict → live snapshot
  //     (RightNow + fleet/alarms) → story → SCADA → chart, with the
  //     engineer-oriented explainer cards (Welcome / ModeReasoning / Tariff)
  //     DEMOTED to render after the live snapshot, just above StoryCards.
  const healthBannerNode = <HealthBanner key="health" />;
  const welcomeNode = <WelcomeCard key="welcome" />;
  const modeReasoningNode = <ModeReasoningCard key="modeReasoning" />;
  // Tariff context — "what is the site about to do about prices" (OVW-11).
  // Renders nothing until the price curve arrives.
  const tariffNode = <TariffWindowBanner key="tariff" />;
  const rightNowNode = <RightNowStrip key="rightNow" compact={dens === "glance"} />;
  const storyNode = <StoryCards key="story" />;
  const scadaNode = (
    <Card key="scada" className="overflow-hidden">
      <CardHeader>
        <CardTitle hint={<GranBadge live label={t("monitor.badge.latestUplink")} />}>{t("monitor.overview.liveEnergyFlow")}</CardTitle>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-text-muted hidden sm:inline">{t("monitor.overview.flowLegend", { kw: IDLE_DEADBAND_W / 1000 })}</span>
          <Link to={carryTo("/flow")} title={t("monitor.overview.expandFlowTitle")} className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] font-medium text-text-muted transition hover:border-accent hover:text-accent">
            <Maximize2 size={11} aria-hidden /><span className="hidden sm:inline">{t("monitor.overview.expandFlow")}</span>
          </Link>
        </div>
      </CardHeader>
      <div className="p-4">
        <SiteFlowDiagram topology={topology} snap={snap} detail={scadaDetail} onDetailChange={setScadaDetail} />
      </div>
    </Card>
  );
  const chartNode = (
    <Card key="chart" className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">
              {t(WINDOW_LABEL_KEY[window])}
            </div>
            <div className="mt-0.5 text-xs text-text-muted">
              {t("monitor.overview.chartDesc.pre")}{" "}
              <code className="font-mono">{t(GRAN_LABEL_KEY[effectiveGran])}</code>
              {t("monitor.overview.chartDesc.post")}
            </div>
          </div>
          {dens === "detail" ? (
            <div className="flex flex-wrap items-center gap-3">
              <SegmentedSelect<PowerWindow>
                label={t("monitor.overview.window")}
                value={window}
                options={(Object.keys(POWER_WINDOWS) as PowerWindow[]).map((k) => ({
                  value: k, short: POWER_WINDOWS[k].short, title: t(WINDOW_LABEL_KEY[k]),
                }))}
                onChange={setWindow}
              />
              <SegmentedSelect<PowerGran>
                label={t("monitor.overview.granularity")}
                value={effectiveGran}
                options={(Object.keys(POWER_GRANS) as PowerGran[]).map((k) => ({
                  value: k,
                  short: POWER_GRANS[k].short,
                  title: t(GRAN_LABEL_KEY[k]),
                  disabled: !allowedGrans.includes(k),
                  disabledHint: t("monitor.overview.notAvailableForWindow", { window: POWER_WINDOWS[window].short }),
                }))}
                onChange={setGran}
              />
              <GranBadge label={`${POWER_WINDOWS[window].short} · ${POWER_GRANS[effectiveGran].short}`} />
              <Link
                to={openInHistoricalHref}
                title={t("monitor.overview.openInHistoricalTitle")}
                className="flex h-7 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] font-medium text-text-muted transition hover:border-accent hover:text-accent"
              >
                {t("monitor.openInHistorical")} <ArrowRight size={11} />
              </Link>
            </div>
          ) : (
            // Glance mode hides the window/granularity controls but keeps a
            // one-click path to the full controls in Historical so an energy
            // manager landing on Glance isn't stranded (F05). The chart still
            // draws at its persisted default below.
            <Link
              to={openInHistoricalHref}
              title={t("monitor.overview.openInHistoricalTitle")}
              className="flex h-7 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] font-medium text-text-muted transition hover:border-accent hover:text-accent"
            >
              {t("monitor.overview.detailedView")} <ArrowRight size={11} />
            </Link>
          )}
        </div>
        {historyLoading ? (
          // Shimmer while FETCHING (DSGN-13) — EmptyState below stays
          // reserved for resolved-empty / error results.
          <ChartSkeleton height={220} />
        ) : chartError ? (
          <EmptyState
            title={t("monitor.overview.loadError.title")}
            detail={t("monitor.overview.loadError.detail", {
              err: historyError instanceof Error && historyError.message ? ` (${historyError.message})` : "",
            })}
            action={
              <button
                onClick={() => refetchHistory()}
                className="rounded-md border border-edge bg-surface px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-accent hover:text-accent"
              >
                {t("common.retry")}
              </button>
            }
            height={220}
          />
        ) : chartEmpty ? (
          <EmptyState
            title={t("monitor.overview.noData.title", { window: POWER_WINDOWS[window].short })}
            detail={t("monitor.overview.noData.detail")}
            height={220}
          />
        ) : (
          <PowerSparkChart seriesData={seriesData} hasPv={hasPv} height={220} />
        )}
      </Card>
  );
  const fleetAlarmsNode = (
    <div key="fleetAlarms" className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <AlarmsCompact />
      </div>
      <Card className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("monitor.overview.bessFleet")}</div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-text-muted">{snap.bess_aggregate.online_count}/{topology.bess.length} {t("status.online")}</span>
            {/* N28 — explicit hub→drill down-link: mirrors the SCADA "Expand"
                chip 1:1 (h-7 border-edge chrome, sm:hidden label) so the
                hub→drill relationship is symmetric (SCADA→/flow, fleet→/bess).
                Reuses nav.bess to avoid net-new translations. */}
            <Link
              to={carryTo("/bess")}
              title={t("monitor.overview.openFleetTitle")}
              className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] font-medium text-text-muted transition hover:border-accent hover:text-accent"
            >
              <span className="hidden sm:inline">{t("nav.bess")}</span> <ArrowRight size={11} aria-hidden />
            </Link>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {snap.bess_per_unit.map((u) => {
            const cap = topology.bess.find((b) => b.id === u.id)?.capacity_kWh ?? 0;
            return <BessTile key={u.id} unit={u} capacity_kWh={cap} />;
          })}
        </div>
      </Card>
    </div>
  );

  // Detail = today's exact order (byte-identical sequence). Glance = verdict →
  // live state → problems, with explainer cards demoted below the snapshot.
  const ordered = dens === "glance"
    ? [
        healthBannerNode,   // verdict
        rightNowNode,       // live state
        fleetAlarmsNode,    // problems + fleet (lifted up from the bottom)
        welcomeNode,        // demoted explainer cards (still render, just lower)
        modeReasoningNode,
        tariffNode,
        storyNode,
        scadaNode,
        chartNode,
      ]
    : [
        healthBannerNode,
        welcomeNode,
        modeReasoningNode,
        tariffNode,
        rightNowNode,
        storyNode,
        scadaNode,
        chartNode,
        fleetAlarmsNode,
      ];

  return (
    <PageShell
      title={t("monitor.overview.title")}
      caption={`${topology.name} · ${topology.bess.length}× BESS · ${(topology.bess.reduce((s, b) => s + b.capacity_kWh, 0)).toFixed(0)} kWh · ${hasPv ? t("monitor.overview.pvConnected") : t("monitor.overview.noPv")}`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <DensityToggle value={dens} onChange={setDensity} />
          <button
            onClick={() => exportSnapshotsCsv(recent, topology, `${topology.shortLabel || topology.name}-last-60min`)}
            disabled={recent.length === 0}
            className="flex h-9 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={13} /> {t("common.exportCsv")} ({recent.length})
          </button>
        </div>
      }
    >
      {ordered}
    </PageShell>
  );
}

/**
 * View-density toggle (F05) — a 2-segment radiogroup mirroring
 * SegmentedSelect's markup so it reads as a real toggle, not a plain
 * button. "Glance" trims the Overview to the primary readings; "Detail"
 * restores every section. A leading Layers icon distinguishes it from the
 * adjacent Export button.
 */
function DensityToggle({
  value,
  onChange,
}: {
  value: Density;
  onChange: (v: Density) => void;
}) {
  const t = useT();
  const segments: { value: Density; label: string }[] = [
    { value: "glance", label: t("monitor.overview.density.glance") },
    { value: "detail", label: t("monitor.overview.density.detail") },
  ];
  return (
    <div
      className="inline-flex items-center overflow-hidden rounded-md border border-edge bg-surface"
      role="radiogroup"
      aria-label={t("monitor.overview.density.label")}
    >
      <span className="grid h-7 place-items-center pl-2 pr-1 text-text-faint">
        <Layers size={12} aria-hidden />
      </span>
      {segments.map((s) => {
        const active = s.value === value;
        return (
          <button
            key={s.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-pressed={active}
            onClick={() => onChange(s.value)}
            className={cn(
              "h-7 min-w-[36px] border-l border-edge px-2 text-[11px] font-medium transition",
              active && "bg-accent/15 text-accent",
              !active && "text-text-muted hover:bg-surface-2 hover:text-text",
            )}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact segmented control — one row of buttons with a leading label.
 * The currently-selected option gets the accent treatment; disallowed
 * options render greyed-out and emit a tooltip explaining why so the
 * operator understands the constraint (e.g. "2 min not available for
 * the 30-day window — too many buckets to render readably").
 */
function SegmentedSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; short: string; title?: string; disabled?: boolean; disabledHint?: string }[];
  onChange: (v: T) => void;
}) {
  const t = useT();
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{label}</span>
      <div className="inline-flex overflow-hidden rounded-md border border-edge bg-surface">
        {options.map((o) => {
          const active = o.value === value;
          const isDisabled = o.disabled;
          return (
            <button
              key={o.value}
              type="button"
              disabled={isDisabled}
              onClick={() => onChange(o.value)}
              title={isDisabled ? (o.disabledHint ?? t("monitor.overview.unavailableForWindow")) : o.title}
              className={cn(
                "h-7 min-w-[36px] border-l border-edge px-2 text-[11px] font-medium transition first:border-l-0",
                active && "bg-accent/15 text-accent",
                !active && !isDisabled && "text-text-muted hover:bg-surface-2 hover:text-text",
                isDisabled && "cursor-not-allowed text-text-faint opacity-50",
              )}
            >
              {o.short}
            </button>
          );
        })}
      </div>
    </div>
  );
}
